import { useState, useEffect } from 'react'
import { Plus, CreditCard, Trash2, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { addDebt, removeDebt } from '@/store/slices/debtSlice'
import { visitPage } from '@/store/slices/gamificationSlice'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import { getDebtPriority, type DebtType } from '@/types'

export default function Debt() {
  const dispatch = useAppDispatch()
  const debt = useAppSelector(state => state.debt)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('debt'))
  }, [dispatch])

  const [newDebt, setNewDebt] = useState({
    name: '',
    debtType: 'credit_card' as DebtType,
    balance: 0,
    interestRate: 0,
    monthlyPayment: 0,
    creditLimit: 0,
  })

  const handleAddDebt = () => {
    if (!newDebt.name) return
    
    dispatch(addDebt({
      ...newDebt,
      paymentPercentage: 2,
    }))
    
    setNewDebt({
      name: '',
      debtType: 'credit_card',
      balance: 0,
      interestRate: 0,
      monthlyPayment: 0,
      creditLimit: 0,
    })
    setDialogOpen(false)
  }

  const handleRemoveDebt = (id: number) => {
    dispatch(removeDebt(id))
  }

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive">High Priority</Badge>
      case 'medium':
        return <Badge variant="warning">Medium</Badge>
      default:
        return <Badge variant="success">Low</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Debt Manager" 
        description="Track and manage your debts with payoff strategies."
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Debt
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Debt</DialogTitle>
              <DialogDescription>
                Add a new debt to track and manage.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="Credit Card Name"
                    value={newDebt.name}
                    onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={newDebt.debtType}
                    onValueChange={(value: DebtType) => setNewDebt({ ...newDebt, debtType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="personal_loan">Personal Loan</SelectItem>
                      <SelectItem value="student_loan">Student Loan</SelectItem>
                      <SelectItem value="auto_loan">Auto Loan</SelectItem>
                      <SelectItem value="medical">Medical</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Balance</Label>
                  <Input
                    type="number"
                    value={newDebt.balance || ''}
                    onChange={(e) => setNewDebt({ ...newDebt, balance: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Interest Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newDebt.interestRate || ''}
                    onChange={(e) => setNewDebt({ ...newDebt, interestRate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monthly Payment</Label>
                  <Input
                    type="number"
                    value={newDebt.monthlyPayment || ''}
                    onChange={(e) => setNewDebt({ ...newDebt, monthlyPayment: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                {newDebt.debtType === 'credit_card' && (
                  <div className="space-y-2">
                    <Label>Credit Limit</Label>
                    <Input
                      type="number"
                      value={newDebt.creditLimit || ''}
                      onChange={(e) => setNewDebt({ ...newDebt, creditLimit: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddDebt}>Add Debt</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Debt"
          value={formatCurrency(debt.totalBalance)}
          icon={CreditCard}
          changeType="negative"
        />
        <StatCard
          title="Monthly Payments"
          value={formatCurrency(debt.totalMonthlyPayments)}
          icon={CreditCard}
        />
        <StatCard
          title="Avg Interest Rate"
          value={formatPercentage(debt.averageInterestRate)}
          icon={AlertTriangle}
          changeType={debt.averageInterestRate > 15 ? 'negative' : 'neutral'}
        />
        <StatCard
          title="Credit Utilization"
          value={formatPercentage(debt.totalCreditUtilization)}
          icon={CreditCard}
          changeType={debt.totalCreditUtilization > 30 ? 'negative' : 'positive'}
        />
      </div>

      {/* Debt List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Debts</CardTitle>
          <CardDescription>Track all your debts and payment progress</CardDescription>
        </CardHeader>
        <CardContent>
          {debt.debts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No debts tracked yet. Add your first debt to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Payment</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {debt.debts.map((d) => {
                  const priority = getDebtPriority(d)
                  return (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {d.debtType.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-destructive">
                        {formatCurrency(d.balance)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatPercentage(d.interestRate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(d.monthlyPayment)}
                      </TableCell>
                      <TableCell>{getPriorityBadge(priority)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveDebt(d.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Debt Analysis */}
      {debt.debts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Debt Analysis</CardTitle>
            <CardDescription>Projected payoff timeline and interest costs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Total Interest to Pay</p>
                <p className="text-2xl font-bold text-destructive">
                  {formatCurrency(debt.debtAnalysis.totalInterestToPay)}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Monthly Interest</p>
                <p className="text-2xl font-bold text-warning">
                  {formatCurrency(debt.debtAnalysis.monthlyInterestExpense)}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Months to Payoff</p>
                <p className="text-2xl font-bold">
                  {debt.debtAnalysis.monthsToPayoff > 0 ? `${debt.debtAnalysis.monthsToPayoff} mo` : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
